// Mitmach-Module: Jedes Radio schaltet ein, was es nutzen möchte.
//
// Ausgeschaltet heißt wirklich aus: Die Blöcke stehen nicht in der Blockauswahl,
// die Hörerseite zeichnet sie nicht, und die Endpunkte antworten mit „gibt es nicht“.

import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { BlockForm } from "../components/block-fields";
import { errorText } from "../components/streaming-ui";
import { ChartReport, Meldungen, RatingReport, Schalter, Sym } from "../components/social-ui";
import { Badge, Callout, Page, PageHeader, Skeleton } from "../components/ui";
import { ReminderReportView } from "../components/reminders-ui";

type Module = Awaited<ReturnType<typeof api.listModules>>[number];

/** Module, zu denen es in der Suite etwas anzuschauen gibt. */
const REPORTS: Record<string, JSX.Element | undefined> = {
  ratings: <RatingReport />,
  charts: <ChartReport />,
  reminders: <ReminderReportView />,
};

/** Ein Symbol je Modul, damit man die Liste auch mit einem Blick überfliegt. */
const SYMBOL: Record<string, string> = {
  poll: "poll",
  charts: "trend",
  greetings: "heart",
  chat: "chat",
  phone: "phone",
  ratings: "star",
  contest: "gift",
  reminders: "bell",
  events: "calendar",
  voice_survey: "mic",
  quiz: "help",
  veranstaltung: "party",
};

export function ModulePage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [modules, setModules] = useState<Module[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    api.listModules().then(setModules).catch((err) => setError(errorText(err)));
  }, []);

  const save = async (id: string, patch: { enabled?: boolean; settings?: Record<string, unknown> }): Promise<void> => {
    setError(null);
    try {
      const saved = await api.saveModule(id, patch);
      setModules((list) => (list ?? []).map((m) => (m.id === id ? { ...m, enabled: saved.enabled, settings: saved.settings } : m)));
      setNotice(patch.enabled === undefined ? "Gespeichert." : saved.enabled ? "Eingeschaltet." : "Ausgeschaltet.");
    } catch (err) {
      setError(errorText(err));
    }
  };

  if (!modules) {
    return (
      <Page className="max-w-5xl">
        <PageHeader eyebrow="Aussehen" title="Mitmach-Module" />
        {error ? <Callout tone="danger">{error}</Callout> : (
          <div className="space-y-3" aria-label="Lade …">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
        )}
      </Page>
    );
  }

  const an = modules.filter((m) => m.enabled).length;

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Aussehen"
        title="Mitmach-Module"
        description={<>
          Jedes Modul bringt etwas, womit Hörer mitmachen können. Schaltet ein, was zu eurem Radio passt – der Rest
          bleibt unsichtbar: keine Blöcke in der Seitenauswahl, nichts auf der Hörerseite, nichts im Studio.
          {!isAdmin && " Ändern dürfen das nur Admins."}
        </>}
        actions={<Badge tone={an > 0 ? "brand" : "neutral"}>{an} von {modules.length} an</Badge>}
      />
      <Meldungen error={error} notice={notice} onClear={() => setNotice(null)} />

      <ul className="space-y-3">
        {modules.map((m) => (
          <li key={m.id} className={`card overflow-hidden transition-shadow ${m.enabled ? "" : "opacity-[0.92]"}`}>
            <div className="flex flex-wrap items-start gap-4 p-4 sm:p-5">
              <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${m.enabled ? "bg-brand-primary/10 text-brand-primary" : "bg-input text-muted"}`}
                style={m.enabled ? undefined : { boxShadow: "inset 0 0 0 1px var(--border)" }}>
                <Sym name={SYMBOL[m.id] ?? "sparkles"} className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1 basis-56">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-[0.95rem] font-semibold text-primary">{m.label}</h2>
                  <Badge tone={m.enabled ? "success" : "neutral"} dot={m.enabled}>{m.enabled ? "an" : "aus"}</Badge>
                  {m.studio && <Badge tone="neutral"><Sym name="mic" className="h-3 w-3" />auch im Studio</Badge>}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-muted">{m.hint}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                {m.enabled && REPORTS[m.id] && (
                  <button onClick={() => setReport(report === m.id ? null : m.id)} className={`btn btn-sm ${report === m.id ? "btn-secondary" : "btn-ghost"}`}>
                    <Sym name="poll" className="h-3.5 w-3.5" />
                    {report === m.id ? "Auswertung zu" : "Auswertung"}
                  </button>
                )}
                {m.enabled && m.fields.length > 0 && (
                  <button onClick={() => setOpen(open === m.id ? null : m.id)} className={`btn btn-sm ${open === m.id ? "btn-secondary" : "btn-ghost"}`}>
                    <Sym name="sliders" className="h-3.5 w-3.5" />
                    {open === m.id ? "Schließen" : "Einstellen"}
                  </button>
                )}
                <span className="ml-1 flex items-center gap-2">
                  <span className="sr-only">{m.enabled ? "Ausschalten" : "Einschalten"}</span>
                  <Schalter
                    checked={m.enabled}
                    disabled={!isAdmin}
                    label={m.enabled ? `${m.label} ausschalten` : `${m.label} einschalten`}
                    onChange={() => void save(m.id, { enabled: !m.enabled })}
                  />
                </span>
              </div>
            </div>

            {m.id === "greetings" && m.enabled && m.settings.auto === true && (
              <div className="px-4 pb-4 sm:px-5">
                <Callout tone="warning" title="Achtung: „Ohne Freigabe zeigen“ ist an.">
                  Jeder Text steht sofort öffentlich auf der Hörerseite –
                  auch Beleidigungen und Werbung. Ihr seid dafür verantwortlich. Im Nachrichten-Eingang oder im
                  Studio nehmt ihr einen Gruß mit „Von der Wand“ sofort wieder herunter.
                </Callout>
              </div>
            )}

            {report === m.id && m.enabled && REPORTS[m.id] && (
              <div className="border-t border-default bg-surface-app/40 p-4 sm:p-5">
                <div className="section-label mb-3">Auswertung</div>
                {REPORTS[m.id]}
              </div>
            )}

            {open === m.id && m.enabled && (
              <div className="border-t border-default p-4 sm:p-5">
                <div className="section-label mb-3">Einstellungen</div>
                <div className="max-w-2xl">
                  <BlockForm
                    fields={m.fields}
                    props={m.settings}
                    onChange={(settings) => setModules((list) => (list ?? []).map((x) => (x.id === m.id ? { ...x, settings } : x)))}
                    channels={[]}
                    publicUrl=""
                  />
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button disabled={!isAdmin} onClick={() => void save(m.id, { settings: m.settings })} className="btn btn-primary">
                    Einstellungen speichern
                  </button>
                  <button onClick={() => setOpen(null)} className="btn btn-ghost">Schließen</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Page>
  );
}
